/**
 * Whether the sheet's problems have been looked for yet.
 *
 * Validation is a gate you run, not a thing that nags while you read. A freshly
 * parsed plan shows no red at all until pre-flight has been asked for — which
 * keeps a first read about "is this the right plan" rather than a wall of
 * complaints about a file nobody has approved yet.
 *
 * `stale` is the important one: editing after a run keeps the findings on screen
 * so the operator can work through the list, but withdraws the pass, so
 * publishing always follows a check of what is actually about to go out.
 */
export type PreflightStatus = 'idle' | 'running' | 'checked' | 'stale';

/** Findings are on screen for both `checked` and `stale`. */
export function showsIssues(status: PreflightStatus): boolean {
  return status === 'checked' || status === 'stale';
}

/** An edit withdraws the pass but never hides the worklist mid-fix. */
export function afterEdit(status: PreflightStatus): PreflightStatus {
  return showsIssues(status) ? 'stale' : status;
}

/**
 * Whether a finding still describes the field it sits on.
 *
 * A finding quotes the value it was computed against ("expected lifetime, found
 * daily"). Once the operator changes that field the quote is wrong, and a message
 * contradicting the cell next to it reads as a broken editor rather than a stale
 * check. Keeping the worklist is right; asserting the old value is not.
 *
 * Scoped to fields edited since the run, so the untouched findings in the list
 * stay fully authoritative.
 */
export function isFindingOutOfDate(status: PreflightStatus, fieldWasEdited: boolean): boolean {
  return status === 'stale' && fieldWasEdited;
}

/**
 * The status a freshly loaded job starts in.
 *
 * Findings live on the server, so a reload must not reset to `idle` and hide a
 * check that has already run — that reads as the findings having been lost.
 * Staleness is the server's call, since only it can compare the last run against
 * the last change to the plan.
 */
export function statusFromJob(summary: { isStale: boolean } | null | undefined): PreflightStatus {
  if (!summary) return 'idle';
  return summary.isStale ? 'stale' : 'checked';
}
